import React, { useState } from "react";
import { Link } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import {
  ChatBubbleLeftRightIcon,
  StarIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import CustomerLayout from "../../../Layouts/CustomerLayout";
import ImageGallery from "../../../Components/Domestic/ImageGallery";
import StarRating from "../../../Components/Domestic/StarRating";

const statusStyles = {
  open: "bg-green-100 text-green-800",
  matched: "bg-blue-100 text-blue-800",
  completed: "bg-purple-100 text-purple-800",
  closed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabel = {
  open: "Open",
  matched: "Matched",
  completed: "Completed",
  closed: "Closed",
  cancelled: "Cancelled",
};

const ReviewForm = ({ requestId }) => {
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function submit(e) {
    e.preventDefault();
    if (!rating) return;
    setSubmitting(true);
    Inertia.post(
      `/domestic/requests/${requestId}/review`,
      { rating, note },
      { onFinish: () => setSubmitting(false) }
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
    >
      <p className="text-sm text-gray-600">
        Once the job&apos;s done, let other customers know how it went.
      </p>
      <div className="mt-3">
        <StarRating value={rating} onChange={setRating} size="h-7 w-7" />
      </div>
      <textarea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Anything worth mentioning? (optional)"
        className="mt-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm"
      />
      <button
        type="submit"
        disabled={!rating || submitting}
        className="mt-3 rounded-md bg-orange-600 py-2 px-4 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {submitting ? "Submitting…" : "Submit review"}
      </button>
    </form>
  );
};

const Show = ({ request, responses, matching_tradesmen, review }) => {
  const isOpen = request.status === "open";
  const canReview = request.status === "matched" || request.status === "completed";

  function acceptResponse(responseId, tradesmanName) {
    if (
      !window.confirm(
        `Accept ${tradesmanName}? Other tradespeople will be told you went with someone else.`
      )
    )
      return;
    Inertia.post(`/domestic/requests/${request.id}/accept_response`, {
      response_id: responseId,
    });
  }

  return (
    <CustomerLayout title="Request details">
      <div className="max-w-3xl space-y-8">
        <Link
          href="/domestic/requests"
          className="text-sm font-medium text-orange-700 hover:text-orange-600"
        >
          &larr; Back to my requests
        </Link>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {request.trades.join(", ")}
            </h2>
            <span
              className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
                statusStyles[request.status] || statusStyles.closed
              }`}
            >
              {statusLabel[request.status] || "Closed"}
            </span>
          </div>
          <p className="mt-4 whitespace-pre-wrap text-gray-700">
            {request.description}
          </p>
          <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-gray-100 pt-4 text-xs font-medium uppercase tracking-wide text-gray-400">
            <div>
              <dt>Postcode district</dt>
              <dd className="mt-0.5 text-sm normal-case tracking-normal text-gray-900">
                {request.postcode_district_code || "—"}
              </dd>
            </div>
            <div>
              <dt>Ideal start date</dt>
              <dd className="mt-0.5 text-sm normal-case tracking-normal text-gray-900">
                {request.ideal_start_date || "—"}
              </dd>
            </div>
          </dl>

          {request.images && request.images.length > 0 && (
            <div className="mt-5 border-t border-gray-100 pt-5">
              <ImageGallery images={request.images} />
            </div>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Tradespeople who match this job
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Cover your postcode and trades — browse their profile and
            contact them directly.
          </p>
          {matching_tradesmen.length === 0 ? (
            <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-white px-5 py-6">
              <p className="text-sm text-gray-500">
                No matching tradespeople yet — check back soon.
              </p>
            </div>
          ) : (
            <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {matching_tradesmen.map((tm) => (
                <li key={tm.id}>
                  <Link
                    href={`/domestic/requests/${request.id}/tradesmen/${tm.id}`}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-orange-300 hover:bg-orange-50/30"
                  >
                    {tm.avatar_url ? (
                      <img
                        src={tm.avatar_url}
                        alt=""
                        className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="h-12 w-12 flex-shrink-0 text-gray-300" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {tm.name}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {tm.trades.join(", ")}
                      </p>
                      {tm.star_rating && (
                        <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                          <StarIcon className="h-3.5 w-3.5 text-yellow-400" />
                          {tm.star_rating.toFixed(1)}
                        </p>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Responses from tradespeople
          </h3>
          {responses.length === 0 ? (
            <div className="mt-3 flex items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-5 py-6">
              <ChatBubbleLeftRightIcon className="h-6 w-6 flex-shrink-0 text-gray-300" />
              <p className="text-sm text-gray-500">
                No responses yet — local tradespeople matching your trades and
                area will see your request and get in touch here.
              </p>
            </div>
          ) : (
            <ul className="mt-3 space-y-4">
              {responses.map((resp) => (
                <li
                  key={resp.id}
                  className={`rounded-lg border p-5 ${
                    resp.status === "accepted"
                      ? "border-green-200 bg-green-50"
                      : resp.status === "declined"
                      ? "border-gray-100 bg-gray-50 opacity-60"
                      : "border-gray-200 bg-white shadow-sm"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-base font-semibold text-gray-900">
                      {resp.tradesman_name}
                      {resp.status === "accepted" && (
                        <span className="ml-2 inline-flex rounded-full bg-green-100 px-2 py-0.5 align-middle text-[11px] font-medium text-green-800">
                          Accepted
                        </span>
                      )}
                      {resp.status === "declined" && (
                        <span className="ml-2 inline-flex rounded-full bg-gray-100 px-2 py-0.5 align-middle text-[11px] font-medium text-gray-500">
                          Not chosen
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-400">{resp.created_at}</p>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                    {resp.message}
                  </p>
                  {resp.quote_amount && (
                    <p className="mt-2 text-lg font-semibold text-gray-900">
                      £{resp.quote_amount}
                    </p>
                  )}
                  {resp.status === "accepted" && resp.tradesman_contact && (
                    <div className="mt-3 rounded-md border border-green-200 bg-white p-3 text-sm">
                      <p className="font-semibold text-gray-900">Contact details</p>
                      <p className="text-gray-700">{resp.tradesman_contact.email}</p>
                      {resp.tradesman_contact.phone && (
                        <p className="text-gray-700">{resp.tradesman_contact.phone}</p>
                      )}
                    </div>
                  )}
                  {isOpen && (
                    <button
                      type="button"
                      onClick={() => acceptResponse(resp.id, resp.tradesman_name)}
                      className="mt-3 rounded-md bg-orange-600 py-1.5 px-4 text-sm font-medium text-white hover:bg-orange-700"
                    >
                      Accept this tradesperson
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canReview && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {review ? "Your review" : "How did it go?"}
            </h3>
            {review ? (
              <div className="mt-3 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                <StarRating value={review.rating} />
                {review.note && (
                  <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">
                    {review.note}
                  </p>
                )}
                <p className="mt-3 text-xs text-gray-400">
                  Reviewed {review.created_at}
                </p>
              </div>
            ) : (
              <ReviewForm requestId={request.id} />
            )}
          </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default Show;
